import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Lightbulb,
  Users,
  Target,
  Heart,
  BookOpen,
  Award,
  CheckCircle,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/pf8whese_Image%20%2C%2000_45_27.png";
const METHODOLOGY_IMAGE = "https://images.unsplash.com/photo-1603354350317-6f7aaa5911c5?w=700&h=500&fit=crop&auto=format";

const methodology = [
  {
    step: "01",
    title: "Discover",
    desc: "Kids explore new concepts through interactive demos and fun challenges that spark curiosity.",
    color: "#1677FF",
  },
  {
    step: "02",
    title: "Create",
    desc: "They apply what they've learned by building real projects — games, robots, animations, and more.",
    color: "#F28C38",
  },
  {
    step: "03",
    title: "Collaborate",
    desc: "Students work in small teams, share ideas, and give each other feedback in a supportive environment.",
    color: "#00A676",
  },
  {
    step: "04",
    title: "Showcase",
    desc: "Every child presents their project, celebrating what they've built and boosting their confidence.",
    color: "#D52B1E",
  },
];

const values = [
  { icon: Heart, title: "Passion for Teaching", desc: "Our instructors love what they do and it shows in every class.", color: "#D52B1E", bg: "#FFE9E7" },
  { icon: Users, title: "Inclusive Environment", desc: "Every child is welcome regardless of experience or background.", color: "#1677FF", bg: "#EAF2FF" },
  { icon: Target, title: "Growth Mindset", desc: "We celebrate effort, creativity, and progress, not just results.", color: "#00A676", bg: "#E7FFF6" },
  { icon: BookOpen, title: "Curriculum Excellence", desc: "Project-based curriculum developed by education and tech experts.", color: "#F28C38", bg: "#FFF1E6" },
  { icon: Award, title: "Safety First", desc: "Background-checked staff and safe, supervised learning spaces.", color: "#9333EA", bg: "#F3E8FF" },
  { icon: Lightbulb, title: "Innovation", desc: "We stay current with the latest trends and tools in edtech and STEM education.", color: "#F7D154", bg: "#FFF7D6" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Banner */}
      <section className="pt-[35px] pb-[35px] md:py-24 hero-gradient noise-bg">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#FFE9E7] text-[#D52B1E] mb-4">
                <Sparkles className="w-3 h-3" /> About Us
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Building Tomorrow's{" "}
                <span className="text-[#1677FF]">Innovators</span> Today
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                Maple Code Academy was founded with a simple mission: make coding, robotics, and digital creativity accessible and fun for every child. We've helped hundreds of students from around the world discover their passion for technology.
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Our expert instructors create a supportive, engaging environment where children build real projects, develop critical thinking skills, and gain the confidence to tackle any challenge.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-3 bg-[#FFF1E6] rounded-[2rem] -rotate-2"></div>
              <img
                src={ABOUT_IMAGE}
                alt="Students learning at Maple Code Academy"
                className="relative rounded-2xl w-full h-[350px] object-cover shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Methodology Timeline */}
      <section className="pt-[35px] pb-[35px] md:py-24 bg-white">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#EAF2FF] text-[#1677FF] mb-4">
              Our Approach
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
              How We{" "}
              <span className="text-[#F28C38]">Teach</span>
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Our four-step methodology ensures every child learns effectively while having a blast.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodology.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="text-5xl font-bold opacity-10 mb-3" style={{ color: step.color, fontFamily: "var(--font-accent)" }}>
                  {step.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed sm:max-w-[240px]">{step.desc}</p>
                {i < methodology.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 text-border">
                    <ChevronRight className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="pt-[35px] pb-[35px] md:py-24 bg-[#FBFBF7]">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#E7FFF6] text-[#00A676] mb-4">
              Our Values
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              What Drives{" "}
              <span className="text-[#00A676]">Us</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-border p-6 pr-[34px] card-hover"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: v.bg }}
                >
                  <v.icon className="w-6 h-6" style={{ color: v.color }} />
                </div>
                <h3 className="font-bold text-base mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-[35px] pb-[35px] md:py-20 bg-[#1677FF] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-white/5"></div>
          <div className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full bg-white/5"></div>
        </div>
        <div className="relative mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to See the Difference?
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-lg mx-auto">
            Book a free trial class and see why parents trust Maple Code Academy.
          </p>
          <Link to="/contact">
            <Button size="lg" className="rounded-full bg-white text-[#1677FF] hover:bg-white/90 btn-lift px-8 h-12 text-base font-bold shadow-xl">
              Book a Free Trial
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
